import { debounce } from "lodash.throttle";

import { EventHelper } from "UI/UI.BaseControls/Helpers/EventHelper";
import { setExtendedPropertiesOnElement } from "UI/utils";
import { DataConverter } from "../../../../../Global/Base/Converters/DataConverter";
import { EMPTY_ITEM_RIGHTS_COLLECTION } from "../../../../../Global/Base/Rights/RightTypes";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { ActionType, IAction, IWFAction } from "../../../../UI.Controls/Actions/IAction";
import { INodeAction } from "../../../../UI.Controls/Actions/INodeAction";
import { DropDownMenuItem } from "../../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { createElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Control } from "../../Controls/Control";
import { Icon, MenuTypes } from "../../FormUI/FormUI";
import { IToolbarButton } from "./IToolbarButton";
import { ToolbarMenuButton } from "./ToolbarMenuButton";

/**Reprezentuje tlacitko v toolbaru*/
export class DesktopToolbarButton extends Control implements IToolbarButton {
    public static readonly CLICK_DEBOUNCE_TIME = 300;

    //#region Fields
    protected _menuType: MenuTypes;
    protected _action: IAction;
    protected _iconElement: HTMLElement;
    protected _captionElement: HTMLDivElement;
    protected _enabledVisual: boolean = true;

    private __priority: number;
    private __icon: string | Icon;
    private __isWF: boolean;

    private _notificationElement: HTMLDivElement;
    private _notificationOldCaption: string;
    private _runningLocalizationPromise: Promise<void> | null;
    //#endregion Fields

    //#region Listeners
    protected _dropdownmenuitem: DropDownMenuItem;
    //#endregion Listeners

    //#region Properties
    public set action(action: IAction) {
        this._removeActionListeners();

        this._action = action;
        if (action) {
            this.enabled = action.enabled;
            this.enabledVisual = action.enabledVisual;

            if (this.action.id)
                this.element.id = `${action.id}-tb`;

            this.element.setAttribute("data-action-type", ActionType[this.action.actionType]);

            if (this.action.extendedProperties)
                setExtendedPropertiesOnElement(this.action.extendedProperties, this.element);

            this._isWF = this.action instanceof IWFAction;
            this._priority = this.action.priority;
            this.locKey = this.action.locKey;

            if (this.action.actionSize)
                this.element.classList.add(this.action.actionSize);

            this.visible = DataConverter.toBoolean(this.element.getAttribute("Visible"), this.action.visible);

            this._dropdownmenuitem.action = this.action;

            this._addActionListeners();
        }
    }

    public get action(): IAction {
        return this._action;
    }

    //#region Visible
    public override set visible(value: boolean) {
        if (this.element == undefined)
            return;

        this._visible = value;
        this._setVisible(value);
        if (this.visibilityChanged)
            this.visibilityChanged.trigger(this, value);
    }

    public override get visible(): boolean {
        return this._visible;
    }
    //#endregion Visible

    public override set enabled(value: boolean) {
        this._enabled = value;
    }

    public override get enabled(): boolean {
        return this._enabled;
    }

    public set enabledVisual(value: boolean) {
        this._enabledVisual = value;

        this._updateElementsEnableState(value);
    }

    public get enabledVisual(): boolean {
        return this._enabledVisual;
    }

    protected set _priority(value: number) {
        this.__priority = value;

        if (this._priority !== undefined && this._priority != null)
            this.element.setAttribute("data-priority", this._priority + "");
    }

    protected get _priority(): number {
        return this.__priority;
    }

    protected get _icon(): string | Icon {
        return this.__icon;
    }

    protected set _icon(value: string | Icon) {
        if (this._iconElement) {
            if (typeof this._icon === "string")
                this._iconElement.classList.remove(this._icon);
            else
                this._iconElement.classList.remove(Icon[this._icon]);
        }

        this.__icon = value;

        if (this._iconElement) {
            if (typeof this._icon === "string")
                this._iconElement.classList.add(this._icon);
            else
                this._iconElement.classList.add(Icon[this._icon]);
        }
    }

    public setIcon(value: string | Icon): void {
        this._icon = value;
    }

    protected set _isWF(value: boolean) {
        this.__isWF = value;

        if (this._isWF)
            this.element.classList.add("workFlowAction");
    }

    protected get _isWF(): boolean {
        return this.__isWF;
    }

    public get dropDownMenuItem(): DropDownMenuItem {
        return this._dropdownmenuitem;
    }
    //#endregion Properties

    //#region Constructors
    constructor(icon: string | Icon, menuType: MenuTypes, action: IAction, classes?: string[], baseType: string = "button") {
        super({ element: document.createElement(baseType), originalRightsDefinition: EMPTY_ITEM_RIGHTS_COLLECTION });

        if (menuType == 1)
            this.element.classList.add("ToolbarButton", "loader-enabled", "btn", "flat");
        else
            this.element.classList.add("ToolbarInnerButton");

        this._dropdownmenuitem = new DropDownMenuItem();

        this.enabledVisual = false;

        this.action = action;

        this._icon = icon;

        if (classes && classes.length > 0) {
            this.element.classList.add(...classes);
        }

        this._menuType = menuType;

        this._createIconElement();
        this._createCaptionElement();
        this.element.setAttribute("data-locKey", this.locKey);

        this._setListeners();
    }
    //#endregion Constructors

    //#region Methods

    /**
     * Metoda, ktera v pripade potreby zobrazi na tlačitku notifikační tečku
     *
     * @param isVisible Stav viditelnosti, ktery chcete nastavit
     */
    public changeVisibilityNotificationDot(isVisible: boolean, captionLocKey?: string): void {
        if (this._notificationElement == null) {
            this._notificationElement = createElement({ classes: "red-dot hidden" }) as HTMLDivElement;
            this.element.classList.add("notification-toolbar-button");
            this.element.appendChild(this._notificationElement);
            this._notificationOldCaption = this.element.title;
        }

        this._notificationElement.classList.toggle("hidden", !isVisible);

        if (captionLocKey) {
            Localization.getLocalizationItem(captionLocKey).then((locItem) => {
                this.element.title = locItem.primaryText;
                this.element.setAttribute("aria-label", locItem.primaryText);
            });
        } else {
            this.element.title = this._notificationOldCaption;
            this.element.setAttribute("aria-label", this._notificationOldCaption);
        }
    }

    /**
     * Odebrání listenerů z akce
     */
    protected _removeActionListeners(): void {
        this._events?.removeByKey("toolbarActionListeners");
    }
    /**
     * Přidání listenerů na akci
     */
    protected _addActionListeners(): void {
        this._events?.addWithKey("toolbarActionListeners", this.action.enabledChanged, (_, value) => this.enabled = value);
        this._events?.addWithKey("toolbarActionListeners", this.action.visibilityChanged, (_, value) => this.visible = value);
        this._events?.addWithKey("toolbarActionListeners", this.action.enabledVisualChanged, (_, value) => this.enabledVisual = value);
    }

    /**
     * Nastaveni eventu
     */
    protected _setListeners(): void {
        //pokud je button zobrazen v contextovem menu, tak pri mouse over skryje subMenu v dalsi urovni
        if (this._menuType == 2) {
            this._events.addListener(this.element, "mouseover", () => {
                if (this.action.isActive && this.action instanceof INodeAction)
                    ((this.action.parentAction as INodeAction).toolbarButton as ToolbarMenuButton).closeSubMenuAndSetNew();
            });
        }

        const onClickDebounced = debounce(async () => {
            this.showLoader();
            this.enabled = false;

            try {
                await this.action.execute();
            } finally {
                this.enabled = this.action.enabled;
                this.hideLoader();
            }
        }, DesktopToolbarButton.CLICK_DEBOUNCE_TIME, { leading: true, trailing: false });

        this._events.addListener(this.element, "click", async (e: MouseEvent) => {
            if (e.button !== 0)
                return; // uprava aby se akce volala jen pri stisku primarniho (leveho) tlacitka, jinak se akce provadel pri stisku jakehokoli tlacitka na mysi

            if (!this.enabledVisual)
                return;

            EventHelper.stopPropagation(e);

            onClickDebounced();
        });
        this._events.addListener(this.element, "keyup", (e) => {
            if (e.keyCode == 13) e.stopPropagation();
        });
    }

    protected _createIconElement(): void {
        this._iconElement = document.createElement("div");

        this._iconElement.classList.add("Icon");

        if (this._menuType == 1) { //toolbar
            this._iconElement.classList.add("menuIcon");
            if (typeof this._icon === "string")
                this._iconElement.classList.add(this._icon);
            else
                this._iconElement.classList.add(Icon[this._icon]);
        }

        //todo: potreba doresit pouziti u context menu
        if (this._menuType == 2) { //kontext. menu
            this._iconElement.classList.add("menuIcon");
            if (typeof this._icon === "string")
                this._iconElement.classList.add(this._icon);
            else
                this._iconElement.classList.add(Icon[this._icon]);
        }

        this.element.appendChild(this._iconElement);
    }

    protected _createCaptionElement(): void {
        this._captionElement = document.createElement("div");
        this._captionElement.classList.add("btn-caption");

        if (this.locKey) {
            this._setDefaultCaption();
        } else {
            if (this.action instanceof IWFAction)
                this._captionElement.textContent = this.action.caption;
        }

        this.element.appendChild(this._captionElement);
    }

    /**
     * nastaveni textu na tlacitku, pokud neni vyplneno, pouzije se nastavený lockey
     * @param text
     */
    public setCaption(text: string = null): void {
        if (text) {
            Localization.remLocData(this._captionElement);
            Localization.remLocData(this.element, false);
            if (!this._runningLocalizationPromise) {
                this._setContentAndTitle(text);
            } else {
                this._runningLocalizationPromise = this._runningLocalizationPromise.then((): void => {
                    this._setContentAndTitle(text);
                    this._runningLocalizationPromise = null;
                });
            }
        } else {
            this._setDefaultCaption();
        }
    }

    private _setContentAndTitle(text: string = null): void {
        this._captionElement.textContent = text;
        this.element.title = text;
        this.element.setAttribute("aria-label", text);
    }

    private _setDefaultCaption(): void /* ?? */ {
        Localization.addLocData(this._captionElement, this.locKey, "tc");
        Localization.addLocDescData(this.element, this.locKey, [ "title", "aria-label" ]);
    }

    private _updateElementsEnableState(value: boolean): void {
        if (value) {
            this.element.classList.add("Enabled");
            this.element.classList.remove("Disabled");
        } else {
            this.element.classList.add("Disabled");
            this.element.classList.remove("Enabled");
        }
    }

    private _setVisible(value: boolean): void {
        if (value) {
            this.element.style.display = "";
        } else {
            this.element.style.display = "none";
        }
    }

    //#endregion Methods

    //#region dispose
    public override dispose(): void {
        this.element?.remove();
        this.action?.dispose();
        this._dropdownmenuitem?.dispose();

        super.dispose();
    }

    //#endregion dispose
}
